export type TermsType = 'INSTALMENTS' | 'NET_TERMS';
export type TermsPeriod = 'MONTHLY';
export type OptionStatus = 'approved' | 'pending' | 'created' | 'denied' | 'error' | string;

export type BnplTerms =
    | { type: 'INSTALMENTS'; period: 'MONTHLY'; number_of_instalments: number }
    | { type: 'NET_TERMS'; duration_days: number };

export type PaymentOption = {
    due_days: number | null;
    intent_uuid?: string;
    cost_excl_vat_amount: 0 | string;
    vat_cost: 0 | string;
    total_amount?: number | string;
    currency?: string;
    status?: OptionStatus;
    terms?: {
        type?: TermsType;
        period?: TermsPeriod | string;
        number_of_instalments?: number | null;
        duration_days?: number | null;
    };
    terms_type?: TermsType;
    terms_period?: TermsPeriod | string;
    number_of_instalments?: number | null;
    selected?: boolean;
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

export function isInstalmentOption(option: PaymentOption): boolean {
    if (option.terms?.type === 'INSTALMENTS' || option.terms_type === 'INSTALMENTS') {
        return true;
    }
    if (option.terms?.type === 'NET_TERMS' || option.terms_type === 'NET_TERMS') {
        return false;
    }
    return option.due_days == null;
}

export function getPaymentOptionTitle(option: PaymentOption, t: Translate): string {
    if (isInstalmentOption(option)) {
        const count = option.number_of_instalments ?? option.terms?.number_of_instalments;
        if (count) {
            return t('instalments_monthly', { count });
        }
        return t('instalments');
    }

    if (option.due_days != null) {
        return `${option.due_days} ${t('days')}`;
    }

    return t('instalments');
}

/** Body for `bnpl-intent`. Flat fields cover the list we already render. */
export function termsForOption(option: PaymentOption): BnplTerms | null {
    const nested = option.terms;
    if (nested?.type === 'INSTALMENTS' && nested.number_of_instalments) {
        return {
            type: 'INSTALMENTS',
            period: 'MONTHLY',
            number_of_instalments: nested.number_of_instalments
        };
    }
    if (nested?.type === 'NET_TERMS' && nested.duration_days != null) {
        return { type: 'NET_TERMS', duration_days: nested.duration_days };
    }
    if (isInstalmentOption(option) && option.number_of_instalments) {
        return {
            type: 'INSTALMENTS',
            period: 'MONTHLY',
            number_of_instalments: option.number_of_instalments
        };
    }
    if (option.due_days != null) {
        return { type: 'NET_TERMS', duration_days: option.due_days };
    }
    return null;
}

/** The customer's earlier choice wins over the seller's due days. */
export function initialOptionIndex(options: PaymentOption[], dueDays: number): number {
    const chosen = options.findIndex((option) => option.selected);
    if (chosen >= 0) {
        return chosen;
    }
    const match = options.findIndex((option) => option.due_days === dueDays);
    return match >= 0 ? match : 0;
}
